'use client';
import React, { useCallback } from 'react';
import styled, { StyledComponents } from '@benzinga/themetron';
import { Watchlist } from '@benzinga/watchlist-manager';
import { StockSymbol } from '@benzinga/session';
import { Button, ButtonVariant, Dropdown, Icon } from '@benzinga/core-ui';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons/faStar';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons/faStar';
import { runningClientSide } from '@benzinga/utils';
import classNames from 'classnames';

export interface WatchlistsDropdownProps {
  buttonText?: string;
  buttonVariant?: ButtonVariant;
  loading?: boolean;
  manageWatchListsURL?: string;
  onAddedSymbolToWatchlist?: (watchlistId: Watchlist | undefined) => void;
  onClick?: () => void;
  onRemovedSymbolFromWatchlist?: (watchlistId: Watchlist | undefined) => void;
  showEmptyList?: boolean;
  symbol: StockSymbol;
  watchlists: Array<Watchlist>;
  variant?: 'default' | 'compact';
  showIcon?: boolean;
}

const ManageWatchlistURL = 'https://www.benzinga.com/profile/portfolio/?action=login&utm_source=watchlist_button';

export const WatchlistsDropdown: React.FC<WatchlistsDropdownProps> = props => {
  const addSymbolToWatchlist = (watchlist: Watchlist) => {
    if (props.onAddedSymbolToWatchlist) {
      props.onAddedSymbolToWatchlist(watchlist);
    }
  };

  const removeSymbolFromWatchlist = (watchlist: Watchlist) => {
    const symbolId = watchlist.symbols.find(item => item.symbol === props.symbol);
    if (symbolId) {
      if (props.onRemovedSymbolFromWatchlist) {
        props.onRemovedSymbolFromWatchlist(watchlist);
      }
    }
  };

  const toggleSymbolInWatchlist = async (watchlist: Watchlist, active: boolean) => {
    if (active) {
      removeSymbolFromWatchlist(watchlist);
    } else {
      addSymbolToWatchlist(watchlist);
    }
  };

  const handleDropdownClick = (watchlist: Watchlist, watchlistHasSymbol: boolean) => {
    toggleSymbolInWatchlist(watchlist, watchlistHasSymbol);
  };

  const watchlistHasSymbol = (watchlist: Watchlist) => {
    return watchlist?.symbols.some(symbolVal => symbolVal.symbol === props.symbol) ?? false;
  };

  const watchlistContainsSymbol = props.watchlists?.some(watchlist => watchlistHasSymbol(watchlist));

  const ToggleButton = useCallback(() => {
    return (
      <Button
        aria-label="Add to watchlist"
        className={classNames('add-to-watchlist-button whitespace-nowrap', {
          active: watchlistContainsSymbol,
          [`${props.variant}`]: props.variant,
        })}
        onClick={props.onClick}
        size={props.variant === 'compact' ? 'sm' : 'md'}
        variant={props.buttonVariant || 'primary'}
      >
        {props.showIcon && props.variant !== 'compact' && (
          <Icon className="watchlist-dropdown-icon mr-1" icon={watchlistContainsSymbol ? faStarSolid : faStarRegular} />
        )}
        {props.variant === 'compact' ? (
          <Icon className="watchlist-dropdown-icon" icon={watchlistContainsSymbol ? faStarSolid : faStarRegular} />
        ) : (
          props.buttonText ?? 'Watch'
        )}
        {props.loading ? (
          <StyledLoader className="rounded-full border-solid border-transparent border-4 w-4 h-4 ml-2" />
        ) : null}
      </Button>
    );
  }, [
    watchlistContainsSymbol,
    props.onClick,
    props.showIcon,
    props.variant,
    props.buttonVariant,
    props.buttonText,
    props.loading,
  ]);

  const WatchlistsOptions = () => {
    if (!props.watchlists?.length && !props.showEmptyList) return null;

    return (
      <div>
        {props.watchlists.map((watchlist: Watchlist) => (
          <div
            className="cursor-pointer focus-outline-none hover-bg-blue-50 list-none px-4 py-1"
            key={watchlist.watchlistId}
            onClick={() => handleDropdownClick(watchlist, watchlistHasSymbol(watchlist))}
          >
            <div className="flex items-center justify-between select-none space-x-1 font-bold text-gray-700 text-sm uppercase">
              {watchlist.name}
              {watchlistHasSymbol(watchlist) ? <Icon className="text-xs text-green-600 ml-1" icon={faCheck} /> : null}
            </div>
          </div>
        ))}
        <hr />
        <div className="pb-1 px-4">
          <ManageWatchListsLink href={props.manageWatchListsURL ?? ManageWatchlistURL} target="_blank">
            Manage watchlists
          </ManageWatchListsLink>
        </div>
      </div>
    );
  };

  let portalElement: null | HTMLBodyElement = null;
  if (runningClientSide()) {
    portalElement = document.querySelector('body');
  }

  return (
    <Dropdown closeWhenClickOutside={false} portalElement={portalElement} target={<ToggleButton />}>
      <WatchlistsOptions />
    </Dropdown>
  );
};

const ManageWatchListsLink = styled.a`
  font-size: 12px;
  color: #929497;
  &:hover {
    text-decoration: underline;
  }
`;

const spinStretch = StyledComponents.keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(720deg);
  }
`;

const StyledLoader = styled.div`
  border-width: 3px;
  border-top-color: #1a79ff;
  border-bottom-color: #1a79ff;
  animation: ${spinStretch} 2s linear infinite;
`;
